import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "primereact/sidebar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import api from "../api/axios";
import "../assets/styles/page.css";

import { Tooltip } from "react-tooltip";
import { GrPrevious, GrNext } from "react-icons/gr";
import { FiDownload, FiTrash2, FiRefreshCw } from "react-icons/fi";
import { TbEyeSearch, TbUserEdit } from "react-icons/tb";

const PAGE_SIZE = 10;
const PRODUCT_LIST_STATE_KEY = "admin.products.list-state.v1";

const PAGE_NAME = "Products";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const defaultFilters = {
  search: "",
  category_id: "",
  sub_category_id: "",
  status: "",
  stock_status: "",
  min_price: "",
  max_price: "",
  sort_by: "created_at",
  sort_order: "DESC",
};

const readProductListState = () => {
  const fallback = {
    filters: { ...defaultFilters },
    page: 1,
    showFilters: false,
  };

  if (typeof window === "undefined") return fallback;

  try {
    const savedState = JSON.parse(
      window.sessionStorage.getItem(PRODUCT_LIST_STATE_KEY)
    );

    if (!savedState || typeof savedState !== "object") return fallback;

    const savedFilters = savedState.filters || {};
    const filters = Object.keys(defaultFilters).reduce((result, key) => {
      result[key] =
        typeof savedFilters[key] === "string"
          ? savedFilters[key]
          : defaultFilters[key];
      return result;
    }, {});

    const savedPage = Number(savedState.page);

    return {
      filters,
      page:
        Number.isInteger(savedPage) && savedPage > 0
          ? savedPage
          : 1,
      showFilters: Boolean(savedState.showFilters),
    };
  } catch {
    return fallback;
  }
};

const Products = () => {
  const navigate = useNavigate();
  const [initialListState] = useState(readProductListState);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  const [filters, setFilters] = useState(initialListState.filters);
  const [debouncedSearch, setDebouncedSearch] = useState(
    initialListState.filters.search
  );
  const [showFilters, setShowFilters] = useState(
    initialListState.showFilters
  );

  const [pagination, setPagination] = useState({
    page: initialListState.page,
    total: 0,
    totalPages: 1,
  });

  const filteredSubCategories = useMemo(() => {
    if (!filters.category_id) return subCategories;

    return subCategories.filter(
      (item) => String(item.category_id) === String(filters.category_id)
    );
  }, [filters.category_id, subCategories]);

  useEffect(() => {
    if (filters.search === debouncedSearch) return undefined;

    const timer = setTimeout(() => {
      setPagination((previous) => ({
        ...previous,
        page: 1,
      }));
      setDebouncedSearch(filters.search);
    }, 350);

    return () => clearTimeout(timer);
  }, [filters.search, debouncedSearch]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        PRODUCT_LIST_STATE_KEY,
        JSON.stringify({
          filters,
          page: pagination.page,
          showFilters,
        })
      );
    } catch {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  }, [filters, pagination.page, showFilters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "category_id" ? { sub_category_id: "" } : {}),
    }));

    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  };

  const fetchMeta = async () => {
    try {
      const [categoryRes, subCategoryRes] = await Promise.all([
        api.get("/admin/products/categories", { headers: getPermissionHeaders("view") }),
        api.get("/admin/products/sub-categories", { headers: getPermissionHeaders("view") }),
      ]);

      setCategories(categoryRes.data?.data || []);
      setSubCategories(subCategoryRes.data?.data || []);
    } catch (error) {
      showRequestError(error, "Failed to fetch filters.");
    }
  };

  const fetchProducts = useCallback(async (page = 1) => {
    try {
      setLoading(true);

      const res = await api.get("/admin/products/products", {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          category_id: filters.category_id || undefined,
          sub_category_id: filters.sub_category_id || undefined,
          status: filters.status || undefined,
          stock_status: filters.stock_status || undefined,
          min_price: filters.min_price || undefined,
          max_price: filters.max_price || undefined,
          sort_by: filters.sort_by,
          sort_order: filters.sort_order,
        },
        headers: getPermissionHeaders("view"),
      });

      setProducts(res.data?.data || []);

      setPagination({
        page: res.data?.pagination?.page || page,
        total: res.data?.pagination?.total || 0,
        totalPages: res.data?.pagination?.totalPages || 1,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Product fetch failed.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    filters.category_id,
    filters.sub_category_id,
    filters.status,
    filters.stock_status,
    filters.min_price,
    filters.max_price,
    filters.sort_by,
    filters.sort_order,
  ]);

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    fetchProducts(pagination.page);
  }, [fetchProducts, pagination.page]);

  const resetFilters = () => {
    setFilters({ ...defaultFilters });
    setDebouncedSearch("");
    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  };

  const activeFilterChips = [];

  if (filters.search) {
    activeFilterChips.push({
      label: `Search: ${filters.search}`,
      key: "search",
    });
  }

  if (filters.category_id) {
    const category = categories.find(
      (item) => String(item.category_id) === String(filters.category_id)
    );

    activeFilterChips.push({
      label: `Category: ${category?.name || filters.category_id}`,
      key: "category_id",
    });
  }

  if (filters.sub_category_id) {
    const subCategory = subCategories.find(
      (item) => String(item.sub_category_id) === String(filters.sub_category_id)
    );

    activeFilterChips.push({
      label: `Sub Category: ${subCategory?.name || filters.sub_category_id}`,
      key: "sub_category_id",
    });
  }

  if (filters.status) {
    activeFilterChips.push({
      label: `Status: ${filters.status}`,
      key: "status",
    });
  }

  if (filters.stock_status) {
    activeFilterChips.push({
      label: `Stock: ${filters.stock_status}`,
      key: "stock_status",
    });
  }

  if (filters.min_price || filters.max_price) {
    activeFilterChips.push({
      label: `Price: ₹${filters.min_price || 0} - ₹${
        filters.max_price || "∞"
      }`,
      key: "price",
    });
  }

  const removeFilter = (key) => {
    setFilters((prev) => {
      const updated = { ...prev };

      switch (key) {
        case "price":
          updated.min_price = "";
          updated.max_price = "";
          break;

        case "category_id":
          updated.category_id = "";
          updated.sub_category_id = "";
          break;

        default:
          updated[key] = "";
      }

      return updated;
    });

    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  };

  const goToPage = (page) => {
    setPagination((previous) => ({
      ...previous,
      page,
    }));
  };

  const refreshSelectedProduct = async (productId) => {
    const res = await api.get(`/admin/products/products/${productId}`, {
      headers: getPermissionHeaders("view"),
    });
    setSelectedProduct(res.data?.data || null);
  };

  const downloadProductExport = async () => {
    try {
      setExportLoading(true);
      const response = await api.get("/admin/products/products/export", {
        headers: getPermissionHeaders("view"),
        responseType: "blob",
      });
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || "product-catalogue.xlsx";
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success("Product catalogue downloaded.");
    } catch (error) {
      showRequestError(error, "Product catalogue could not be downloaded.");
    } finally {
      setExportLoading(false);
    }
  };

  const deleteProduct = (product) => {
    confirmDialog({
      header: "Delete Product",
      message: `Delete "${product.product_name}"? This will also delete product media and option mappings.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await api.delete(`/admin/products/products/${product.product_id}`, {
            headers: getPermissionHeaders("delete"),
          });

          if (selectedProduct?.product_id === product.product_id) {
            setViewOpen(false);
            setSelectedProduct(null);
          }

          toast.success("Product deleted successfully.");
          fetchProducts(pagination.page);
        } catch (error) {
          showRequestError(error, "Failed to delete product.");
        }
      },
    });
  };

  const updateProductStatus = async (product, status) => {
    try {
      await api.patch(`/admin/products/products/${product.product_id}/status`, {
        status,
      }, {
        headers: getPermissionHeaders("edit"),
      });

      toast.success("Product status updated.");
      await fetchProducts(pagination.page);

      if (selectedProduct?.product_id === product.product_id) {
        await refreshSelectedProduct(product.product_id);
      }
    } catch (error) {
      showRequestError(error, "Failed to update product status.");
    }
  };

  const openProductView = async (product) => {
    try {
      const res = await api.get(`/admin/products/products/${product.product_id}`, {
        headers: getPermissionHeaders("view"),
      });
      setSelectedProduct(res.data?.data || product);
      setViewOpen(true);
    } catch (error) {
      showRequestError(error, "Failed to load product details.");
      setSelectedProduct(product);
      setViewOpen(true);
    }
  };

  const isImageMedia = (media) => {
    return !media?.media_type || media.media_type === "image";
  };

  const isVideoMedia = (media) => {
    return media?.media_type === "video";
  };

  const getProductMedia = (product) => {
    return product?.media || [];
  };

  const getProductImages = (product) => {
    return getProductMedia(product).filter(isImageMedia);
  };

  const getProductVideos = (product) => {
    return getProductMedia(product).filter(isVideoMedia);
  };

  const getPrimaryImage = (product) => {
    const images = getProductImages(product);

    return (
      images.find((img) => img.is_primary)?.image_url ||
      images[0]?.image_url ||
      null
    );
  };

  const getMediaCount = (product) => {
    return product?.media?.length || 0;
  };

  const productImages = getProductImages(selectedProduct);
  const productVideos = getProductVideos(selectedProduct);

  return (
    <section className="admin-page products-page premium-products-page">
      <ConfirmDialog />

      <div className="page-header">
        <div>
          <p>Catalogue</p>
          <h1>Products</h1>
        </div>

        <div className="page-actions">
          <button
            type="button"
            className="secondary-action product-export-action"
            onClick={downloadProductExport}
            disabled={exportLoading}
          >
            <FiDownload />
            {exportLoading ? "Preparing Excel..." : "Download Excel"}
          </button>

          <button
            type="button"
            className="secondary-action"
            onClick={() => navigate("/products/bulk-upload")}
          >
            Bulk Upload
          </button>

          <button
            type="button"
            className="primary-action"
            onClick={() => navigate("/products/add")}
          >
            + Add Product
          </button>
        </div>
      </div>

      <div className="product-filter-card">
        <div className="product-filter-top">
          <div>
            <h2>Product Filters</h2>
            <p>{loading ? "Loading products..." : ""}</p>
          </div>

          <div className="filter-actions">
            <button
              type="button"
              className="filter-toggle-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <i
                className={`pi ${
                  showFilters ? "pi-chevron-up" : "pi-sliders-h"
                }`}
              />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button type="button" className="ghost-action" onClick={resetFilters}>
              <FiRefreshCw />
            </button>
          </div>
        </div>

        {activeFilterChips.length > 0 && (
          <div className="active-filter-bar">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.label}
                type="button"
                className="active-filter-chip"
                onClick={() => removeFilter(chip.key)}
              >
                {chip.label}
                <i className="pi pi-times" />
              </button>
            ))}
          </div>
        )}

        {showFilters && (
          <div className="product-filter-grid">
            <label className="filter-wide">
              Search
              <input
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                placeholder="Search product name, SKU, keywords or description..."
              />
            </label>

            <label>
              Category
              <select
                value={filters.category_id}
                onChange={(event) =>
                  updateFilter("category_id", event.target.value)
                }
              >
                <option value="">All categories</option>
                {categories.map((category) => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Sub-category
              <select
                value={filters.sub_category_id}
                onChange={(event) =>
                  updateFilter("sub_category_id", event.target.value)
                }
              >
                <option value="">All sub-categories</option>
                {filteredSubCategories.map((subCategory) => (
                  <option
                    key={subCategory.sub_category_id}
                    value={subCategory.sub_category_id}
                  >
                    {subCategory.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Product Status
              <select
                value={filters.status}
                onChange={(event) => updateFilter("status", event.target.value)}
              >
                <option value="">All statuses</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>

            <label>
              Stock
              <select
                value={filters.stock_status}
                onChange={(event) =>
                  updateFilter("stock_status", event.target.value)
                }
              >
                <option value="">All stock</option>
                <option value="in_stock">In Stock</option>
                <option value="out_of_stock">Out of Stock</option>
              </select>
            </label>

            <label>
              Min Price
              <input
                type="number"
                value={filters.min_price}
                onChange={(event) =>
                  updateFilter("min_price", event.target.value)
                }
                placeholder="₹ Min"
              />
            </label>

            <label>
              Max Price
              <input
                type="number"
                value={filters.max_price}
                onChange={(event) =>
                  updateFilter("max_price", event.target.value)
                }
                placeholder="₹ Max"
              />
            </label>

            <label>
              Sort By
              <select
                value={filters.sort_by}
                onChange={(event) => updateFilter("sort_by", event.target.value)}
              >
                <option value="created_at">Created Date</option>
                <option value="updated_at">Updated Date</option>
                <option value="product_name">Product Name</option>
                <option value="base_price">Base Price</option>
                <option value="final_price">Final Price</option>
                <option value="status">Status</option>
                <option value="stock_status">Stock</option>
              </select>
            </label>

            <label>
              Order
              <select
                value={filters.sort_order}
                onChange={(event) =>
                  updateFilter("sort_order", event.target.value)
                }
              >
                <option value="DESC">Descending</option>
                <option value="ASC">Ascending</option>
              </select>
            </label>
          </div>
        )}
      </div>

      <div className="page-card">
        <div className="table-wrap">
          <table className="admin-table product-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Media</th>
                <th>Templates</th>
                <th style={{ width: "100px", textAlign: "center" }}>
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {!loading && products.length === 0 && (
                <tr>
                  <td colSpan="8" className="empty-cell">
                    No products found.
                  </td>
                </tr>
              )}

              {products.map((product) => {
                const image = getPrimaryImage(product);
                const imageCount = getProductImages(product).length;
                const videoCount = getProductVideos(product).length;

                return (
                  <tr key={product.product_id}>
                    <td>
                      <div className="product-cell">
                        {image ? (
                          <img src={image} alt={product.product_name} />
                        ) : (
                          <div className="product-placeholder">
                            {product.product_name?.charAt(0) || "P"}
                          </div>
                        )}

                        <div>
                          <strong>{product.product_name}</strong>
                          <span>{product.sku || "No SKU"}</span>
                          {product.keywords && <small>{product.keywords}</small>}
                        </div>
                      </div>
                    </td>

                    <td>
                      {product.category?.name || "-"}
                      <br />
                      <small>{product.sub_category?.name || "-"}</small>
                    </td>

                    <td>
                      <strong>
                        ₹{Number(product.final_price || 0).toLocaleString()}
                      </strong>
                      {Number(product.discount_value || 0) > 0 && (
                        <>
                          <br />
                          <small>
                            Base ₹
                            {Number(product.base_price || 0).toLocaleString()}
                          </small>
                        </>
                      )}
                    </td>

                    <td>{product.stock_status || "-"}</td>

                    <td>
                      <select
                        className={`status-select ${product.status || "draft"}`}
                        value={product.status || "draft"}
                        onChange={(event) =>
                          updateProductStatus(product, event.target.value)
                        }
                      >
                        <option value="draft">Draft</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </td>

                    <td>
                      <span>{imageCount} Images</span>
                      <br />
                      <small>{videoCount} Videos</small>
                    </td>

                    <td>
                      <span>{product.option_templates?.length || 0}</span>
                    </td>

                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="ghost-action"
                          data-tooltip-id="viewTip"
                          data-tooltip-content="View"
                          onClick={() => openProductView(product)}
                        >
                          <TbEyeSearch />
                        </button>

                        <button
                          type="button"
                          className="ghost-action"
                          data-tooltip-id="editTip"
                          data-tooltip-content="Edit"
                          onClick={() =>
                            navigate(`/products/edit/${product.product_id}`)
                          }
                        >
                          <TbUserEdit />
                        </button>

                        <button
                          type="button"
                          className="ghost-action"
                          data-tooltip-id="deleteTip"
                          data-tooltip-content="Delete"
                          onClick={() => deleteProduct(product)}
                        >
                          <FiTrash2 />
                        </button>

                        <Tooltip id="viewTip" />
                        <Tooltip id="editTip" />
                        <Tooltip id="deleteTip" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="pagination-bar">
          <button
            type="button"
            className="ghost-action"
            disabled={pagination.page <= 1}
            onClick={() => goToPage(pagination.page - 1)}
          >
            <GrPrevious />
          </button>

          <span>
            Page {pagination.page} of {pagination.totalPages}
          </span>

          <button
            type="button"
            className="ghost-action"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => goToPage(pagination.page + 1)}
          >
            <GrNext />
          </button>
        </div>
      </div>

      <Sidebar
        visible={viewOpen}
        position="right"
        onHide={() => setViewOpen(false)}
        className="premium-product-sidebar"
      >
        {selectedProduct && (
          <div className="product-view-panel">
            <div className="product-view-hero">
              {getPrimaryImage(selectedProduct) ? (
                <img
                  src={getPrimaryImage(selectedProduct)}
                  alt={selectedProduct.product_name}
                />
              ) : (
                <div className="product-view-placeholder">
                  {selectedProduct.product_name?.charAt(0) || "P"}
                </div>
              )}
            </div>

            <div className="product-view-title">
              <span>{selectedProduct.category?.name || "Product"}</span>
              <h2>{selectedProduct.product_name}</h2>
              <p>
                {selectedProduct.short_description || "No short description."}
              </p>
            </div>

            <div className="product-view-price">
              ₹{Number(selectedProduct.final_price || 0).toLocaleString()}
            </div>

            <div className="product-view-meta">
              <div>
                <strong>SKU</strong>
                <span>{selectedProduct.sku || "-"}</span>
              </div>

              <div>
                <strong>Status</strong>
                <select
                  className={`status-select ${
                    selectedProduct.status || "draft"
                  }`}
                  value={selectedProduct.status || "draft"}
                  onChange={(event) =>
                    updateProductStatus(selectedProduct, event.target.value)
                  }
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div>
                <strong>Stock</strong>
                <span>{selectedProduct.stock_status || "-"}</span>
              </div>

              <div>
                <strong>Sub-category</strong>
                <span>{selectedProduct.sub_category?.name || "-"}</span>
              </div>

              <div>
                <strong>Total Media</strong>
                <span>{getMediaCount(selectedProduct)}</span>
              </div>

              {selectedProduct.keywords && (
                <div>
                  <strong>Keywords</strong>
                  <span>{selectedProduct.keywords}</span>
                </div>
              )}

              {selectedProduct.tags && (
                <div>
                  <strong>Tags</strong>
                  <span>{selectedProduct.tags}</span>
                </div>
              )}
            </div>

            <div className="product-view-section">
              <h3>Description</h3>
              <p>{selectedProduct.description || "No description added."}</p>
            </div>

            {productImages.length > 0 && (
              <div className="product-view-section">
                <h3>Product Images</h3>
                <div className="product-view-gallery">
                  {productImages.map((image) => (
                    <img
                      key={image.image_id}
                      src={image.image_url}
                      alt={selectedProduct.product_name}
                    />
                  ))}
                </div>
              </div>
            )}

            {productVideos.length > 0 && (
              <div className="product-view-section">
                <h3>Product Videos</h3>
                <div className="product-view-gallery">
                  {productVideos.map((video) => (
                    <video
                      key={video.image_id}
                      src={video.image_url}
                      controls
                      muted
                      playsInline
                    />
                  ))}
                </div>
              </div>
            )}

            {selectedProduct.option_templates?.length > 0 && (
              <div className="product-view-section">
                <h3>Option Templates</h3>

                <div className="product-view-options">
                  {selectedProduct.option_templates.map((template) => (
                    <div key={template.template_id}>
                      <strong>{template.template_name}</strong>

                      {template.fields?.map((field) => (
                        <div key={field.field_id}>
                          <small>{field.field_name}</small>

                          <div>
                            {field.values?.map((value) => (
                              <span key={value.value_id}>
                                {value.label}
                                {Number(value.price_adjustment || 0) > 0
                                  ? ` +₹${Number(
                                      value.price_adjustment
                                    ).toLocaleString()}`
                                  : ""}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="product-view-actions">
              <button
                type="button"
                className="secondary-action"
                onClick={() => setViewOpen(false)}
              >
                Close
              </button>

              <button
                type="button"
                className="primary-action"
                onClick={() =>
                  navigate(`/products/edit/${selectedProduct.product_id}`)
                }
              >
                Edit Product
              </button>
            </div>
          </div>
        )}
      </Sidebar>
    </section>
  );
};

export default Products;
